import { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { novaGenerateReport, novaDownloadReport } from '../../services/novaService';
import ReportLanguageToggle, { useReportLanguage } from './ReportLanguageToggle';

/* ── Animated Nova sparkle icon (CSS keyframes, not emoji) ── */
const NOVA_ICON_STYLE = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, flexShrink: 0,
};

function NovaSparkleIcon() {
  return (
    <>
      <style>{`
        @keyframes novaBarPulse {
          0%, 100% { opacity: 1; filter: brightness(1); }
          50% { opacity: 0.6; filter: brightness(1.4); }
        }
        .nova-bar-icon { animation: novaBarPulse 2s ease-in-out infinite; }
      `}</style>
      <span className="nova-bar-icon" style={NOVA_ICON_STYLE}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#156082" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71" />
          <circle cx="12" cy="12" r="4" fill="#156082" fillOpacity="0.15" />
        </svg>
      </span>
    </>
  );
}

/* ── Simple closeness scorer for suggestions ── */
function closeness(input, candidate) {
  const a = input.toLowerCase(), b = candidate.toLowerCase();
  if (b.startsWith(a)) return 3;
  if (b.includes(a)) return 2;
  // character overlap
  let score = 0;
  for (const ch of a) { if (b.includes(ch)) score++; }
  return score / Math.max(a.length, 1);
}

/* ── Bar component ── */
export function NovaQuickAnalysisBar({ placeholder, knownEntities, onSubmit, reportType, filterBuilder, entityLabel }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEntity, setModalEntity] = useState(null);

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setError(null); setSuggestions([]);

    const lc = trimmed.toLowerCase();
    // Exact match
    let match = knownEntities.find(e => e.toLowerCase() === lc);
    // Unambiguous startsWith
    if (!match) {
      const starts = knownEntities.filter(e => e.toLowerCase().startsWith(lc));
      if (starts.length === 1) match = starts[0];
    }

    if (!match) {
      // Show suggestions
      const scored = knownEntities
        .map(e => ({ e, s: closeness(trimmed, e) }))
        .filter(x => x.s > 0.3)
        .sort((a, b) => b.s - a.s)
        .slice(0, 3);
      setSuggestions(scored.map(x => x.e));
      setError(`No matching ${entityLabel || 'entity'} found.`);
      return;
    }

    onSubmit(match);
    setModalEntity(match);
    setModalOpen(true);
    setInput('');
  }, [input, knownEntities, onSubmit, entityLabel]);

  const handleSuggestionClick = useCallback((entity) => {
    setError(null); setSuggestions([]);
    onSubmit(entity);
    setModalEntity(entity);
    setModalOpen(true);
    setInput('');
  }, [onSubmit]);

  return (
    <>
      <div style={{
        background: 'white', borderRadius: 12, border: '1px solid #e3e8ef',
        padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <NovaSparkleIcon />
        <input
          className="sup-input"
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}
          placeholder={placeholder || 'Type an entity for quick analysis...'}
          value={input}
          onChange={e => { setInput(e.target.value); setError(null); setSuggestions([]); }}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          style={{
            background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8,
            padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Analyze
        </button>
      </div>
      {error && (
        <div style={{ marginTop: -10, marginBottom: 12, fontSize: 12, color: 'var(--t3)' }}>
          {error}
          {suggestions.length > 0 && (
            <span style={{ marginLeft: 6 }}>
              Did you mean:{' '}
              {suggestions.map((s, i) => (
                <span key={i}>
                  <button
                    type="button"
                    onClick={() => handleSuggestionClick(s)}
                    style={{
                      background: 'var(--teal3, #e6f4f0)', color: 'var(--teal)', border: '1px solid var(--teal)',
                      borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500, marginLeft: 4,
                    }}
                  >
                    {s}
                  </button>
                </span>
              ))}
            </span>
          )}
        </div>
      )}
      {modalOpen && modalEntity && (
        <NovaAnalysisModal
          entity={modalEntity}
          reportType={reportType}
          filterBuilder={filterBuilder}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

/* ── Predictive bar (supplier OR molecule) ── */
export function NovaQuickAnalysisBarPredictive({ placeholder, supplierList, moleculeList, onSubmit }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [ambiguous, setAmbiguous] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEntity, setModalEntity] = useState(null);
  const [modalFilters, setModalFilters] = useState(null);

  const allEntities = useMemo(() => [...new Set([...supplierList, ...moleculeList])], [supplierList, moleculeList]);

  const resolve = useCallback((entity, forceType) => {
    const isSup = supplierList.some(s => s.toLowerCase() === entity.toLowerCase());
    const isMol = moleculeList.some(m => m.toLowerCase() === entity.toLowerCase());
    if (isSup && isMol && !forceType) {
      setAmbiguous(entity);
      return;
    }
    setAmbiguous(null);
    const type = forceType || (isSup ? 'supplier' : 'molecule');
    const filters = type === 'supplier' ? { supplier: entity } : { inn: entity };
    onSubmit(entity);
    setModalEntity(entity);
    setModalFilters(filters);
    setModalOpen(true);
    setInput('');
  }, [supplierList, moleculeList, onSubmit]);

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setError(null); setSuggestions([]); setAmbiguous(null);
    const lc = trimmed.toLowerCase();
    let match = allEntities.find(e => e.toLowerCase() === lc);
    if (!match) {
      const starts = allEntities.filter(e => e.toLowerCase().startsWith(lc));
      if (starts.length === 1) match = starts[0];
    }
    if (!match) {
      const scored = allEntities.map(e => ({ e, s: closeness(trimmed, e) })).filter(x => x.s > 0.3).sort((a, b) => b.s - a.s).slice(0, 3);
      setSuggestions(scored.map(x => x.e));
      setError('No matching supplier or molecule found.');
      return;
    }
    resolve(match);
  }, [input, allEntities, resolve]);

  return (
    <>
      <div style={{
        background: 'white', borderRadius: 12, border: '1px solid #e3e8ef',
        padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <NovaSparkleIcon />
        <input
          className="sup-input"
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}
          placeholder={placeholder || 'Type a supplier or molecule...'}
          value={input}
          onChange={e => { setInput(e.target.value); setError(null); setSuggestions([]); setAmbiguous(null); }}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
        />
        <button type="button" onClick={handleSubmit} style={{
          background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8,
          padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>Analyze</button>
      </div>
      {error && (
        <div style={{ marginTop: -10, marginBottom: 12, fontSize: 12, color: 'var(--t3)' }}>
          {error}
          {suggestions.length > 0 && <span style={{ marginLeft: 6 }}>Did you mean:{' '}
            {suggestions.map((s, i) => (
              <button key={i} type="button" onClick={() => { setError(null); setSuggestions([]); resolve(s); }}
                style={{ background: 'var(--teal3, #e6f4f0)', color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500, marginLeft: 4 }}
              >{s}</button>
            ))}
          </span>}
        </div>
      )}
      {ambiguous && (
        <div style={{ marginTop: -10, marginBottom: 12, fontSize: 12, color: 'var(--navy)' }}>
          &ldquo;{ambiguous}&rdquo; matches both a supplier and molecule. Which?{' '}
          <button type="button" onClick={() => resolve(ambiguous, 'supplier')} style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500, marginLeft: 4 }}>Supplier</button>
          <button type="button" onClick={() => resolve(ambiguous, 'molecule')} style={{ background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 12, padding: '2px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 500, marginLeft: 4 }}>Molecule</button>
        </div>
      )}
      {modalOpen && modalEntity && modalFilters && (
        <NovaAnalysisModal
          entity={modalEntity}
          reportType="predictive"
          filterBuilder={() => modalFilters}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

/* ── Large Nova-style result modal ── */
function NovaAnalysisModal({ entity, reportType, filterBuilder, onClose }) {
  const { instance: msalInstance } = useMsal();
  const navigate = useNavigate();
  const [modalLang, setModalLang] = useState(null); // null = choosing language
  const [phase, setPhase] = useState('choosingLanguage'); // choosingLanguage | generating | ready | error
  const [pptxGen, setPptxGen] = useState(false);
  const [xlsxGen, setXlsxGen] = useState(false);
  const [docxGen, setDocxGen] = useState(false);
  const [summary, setSummary] = useState('');
  const [reportMeta, setReportMeta] = useState(null);

  const startGeneration = useCallback(async (chosenLang) => {
    setModalLang(chosenLang);
    setPhase('generating');
    try {
      const filters = typeof filterBuilder === 'function' ? filterBuilder(entity) : filterBuilder;
      const meta = await novaGenerateReport(msalInstance, { reportType, format: 'pptx', filters, language: chosenLang });
      setReportMeta(meta);
      setSummary(`Analysis complete for "${entity}". Your report is ready.`);
      setPhase('ready');
    } catch {
      setSummary(`Failed to generate analysis for "${entity}". Please try again.`);
      setPhase('error');
    }
  }, [msalInstance, reportType, filterBuilder, entity]);

  const handleDownload = useCallback(async (format) => {
    const setGen = format === 'pptx' ? setPptxGen : format === 'docx' ? setDocxGen : setXlsxGen;
    setGen(true);
    try {
      let meta;
      if (format === 'pptx' && reportMeta) {
        meta = reportMeta;
      } else {
        const filters = typeof filterBuilder === 'function' ? filterBuilder(entity) : filterBuilder;
        meta = await novaGenerateReport(msalInstance, { reportType, format, filters, language: modalLang });
      }
      const blob = await novaDownloadReport(msalInstance, meta.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = meta.filename || `report.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      // Navigate to the relevant analytics page after download
      const REPORT_ROUTE_MAP = {
        'therapeutic-area': '/therapeutic-area-developments',
        'molecule': '/molecule-developments',
        'pharmaceutical-form': '/pharmaceutical-form-developments',
        'supplier-portfolio': '/supplier-portfolio-evolution',
        'predictive': '/predictive-supplier-developments',
        'nl-gap': '/nl-market-gap-analysis',
      };
      const targetPath = REPORT_ROUTE_MAP[reportType];
      if (targetPath) {
        const qp = new URLSearchParams();
        if (reportType === 'therapeutic-area') qp.set('area', entity);
        else if (reportType === 'molecule') qp.set('inn', entity);
        else if (reportType === 'pharmaceutical-form') qp.set('form', entity);
        else if (reportType === 'supplier-portfolio') qp.set('supplier', entity);
        else if (reportType === 'predictive') qp.set('supplier', entity);
        else if (reportType === 'nl-gap') qp.set('inn', entity);
        const qs = qp.toString();
        navigate(qs ? `${targetPath}?${qs}` : targetPath);
      }
    } catch {}
    finally { setGen(false); }
  }, [msalInstance, reportType, filterBuilder, entity, reportMeta, navigate]);

  const modalTitle = phase === 'choosingLanguage'
    ? 'Nova Analysis \u{1F60A}'
    : phase === 'generating'
    ? 'Nova is working for you \u{1F60A}'
    : 'Nova analysis ready \u{1F60A}';

  return (
    <>
      <style>{`
        @keyframes novaModalZoomIn {
          from { transform: scale(0.6); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .nova-modal-zoom { animation: none !important; }
        }
      `}</style>
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }} onClick={onClose}>
        <div className="nova-modal-zoom" onClick={e => e.stopPropagation()} style={{
          background: '#fff', borderRadius: 16, width: '90%', maxWidth: 640, maxHeight: '70vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
          animation: 'novaModalZoomIn 0.25s ease-out',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', borderBottom: '1px solid #e3e8ef',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NovaSparkleIcon />
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{modalTitle}</span>
            </div>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--t3)' }}>&times;</button>
          </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Chat-like bubble */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--teal3, #e6f4f0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13, color: 'var(--teal)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.95l-.71.71M21 12h-1M4 12H3m16.66 7.66l-.71-.71M4.05 4.05l-.71-.71" /></svg>
            </div>
            <div style={{
              background: '#f8fafb', borderRadius: '4px 14px 14px 14px', padding: '12px 16px',
              fontSize: 13, color: 'var(--navy)', lineHeight: 1.6, flex: 1,
            }}>
              {phase === 'choosingLanguage' && (
                <span>Which language for the &ldquo;{entity}&rdquo; report?</span>
              )}
              {phase === 'generating' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14 }} />
                  Analyzing &ldquo;{entity}&rdquo;...
                </span>
              )}
              {(phase === 'ready' || phase === 'error') && summary}
            </div>
          </div>

          {/* Language choice */}
          {phase === 'choosingLanguage' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={() => startGeneration('en')}
                style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                English
              </button>
              <button type="button" onClick={() => startGeneration('nl')}
                style={{ background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                Dutch
              </button>
            </div>
          )}

          {/* Download buttons */}
          {phase === 'ready' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <button type="button" disabled={pptxGen} onClick={() => handleDownload('pptx')}
                className="btn btn-sm btn-outline-primary" style={{ fontSize: 12 }}>
                <i className="bi bi-file-earmark-slides me-1"></i>
                {pptxGen ? 'Generating...' : 'Download PowerPoint'}
              </button>
              <button type="button" disabled={xlsxGen} onClick={() => handleDownload('xlsx')}
                className="btn btn-sm btn-outline-success" style={{ fontSize: 12 }}>
                <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                {xlsxGen ? 'Generating...' : 'Download Excel'}
              </button>
              <button type="button" disabled={docxGen} onClick={() => handleDownload('docx')}
                className="btn btn-sm btn-outline-info" style={{ fontSize: 12 }}>
                <i className="bi bi-file-earmark-word me-1"></i>
                {docxGen ? 'Generating...' : 'Download Word'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
